import { Analytics } from '@cobliteam/analytics';
import type { TriggerEventNotification } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/events';
import { Location } from '@cobliteam/common-types';
import { useMarkNotificationAsRead } from '@cobliteam/hooks/src/useEntity/useAlerts';
import { useTranslation } from '@cobliteam/i18n';
import { Button, DropdownMenu, Typography } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { formatRelativeDate } from '@cobliteam/utils/date';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getTriggerEventInfo, getTriggerPrimaryButton } from './utils';

interface TriggerNotificationItemProps {
  triggerNotification: TriggerEventNotification;
  isRead: boolean;
}

const TriggerNotificationItem = ({ triggerNotification, isRead }: TriggerNotificationItemProps) => {
  const { t } = useTranslation('app-header');
  const { createdAt, readAt, triggerEvent, id } = triggerNotification;
  const { trigger, eventTime, meta } = triggerEvent;
  const { conditionCategory, vehicle, driver, address } = meta;
  const { name } = trigger;

  const [markAsRead, setMarkAsRead] = useState(Boolean(readAt) || isRead);
  const { mutate } = useMarkNotificationAsRead(id, createdAt);

  const handleMarkAsRead = () => {
    if (markAsRead) return;

    mutate();
    setMarkAsRead(true);
  };

  const triggerEventInfo = getTriggerEventInfo(triggerEvent);
  if (!(conditionCategory && triggerEventInfo)) return null;

  const { icon, rule, condition, eventType, path } = triggerEventInfo;

  return (
    <div className={cn('m-2.5 space-y-2.5 rounded border border-gray-light p-2.5', markAsRead ? 'hover:bg-gray-lightest' : 'bg-primary-2')} onMouseEnter={handleMarkAsRead}>
      <div className='flex items-center justify-between gap-2.5'>
        <div className='flex items-center gap-1'>
          {!markAsRead && <Icon name='circle' color='primary' size='xs' className='fill-primary' />}
          <Typography variant='caption-medium' color='secondary'>
            {rule}
          </Typography>
        </div>
        <Typography variant='chip-label' color='secondary'>
          {formatRelativeDate(new Date(eventTime), new Date(), '-')}
        </Typography>
      </div>
      <div className='flex items-center gap-1'>
        {icon}
        <Typography variant='description-title' color={markAsRead ? 'secondary' : 'primary'}>
          {name}
        </Typography>
      </div>

      <div>
        <Typography variant='caption-default' color='secondary'>
          <b>{t('notificationDetails.condition')}: </b> {condition}
        </Typography>

        <Typography variant='caption-default' color='secondary'>
          <b>{t('notificationDetails.vehicle')}: </b> {vehicle?.licensePlate} - {vehicle?.model}
        </Typography>

        <Typography variant='caption-default' color='secondary'>
          <b>{t('notificationDetails.driver')}: </b> {driver?.name || t('notificationDetails.noDriverAssociated')}
        </Typography>

        <Typography variant='caption-default' color='secondary'>
          <b>{t('notificationDetails.address')}: </b> {address}
        </Typography>
      </div>

      <div className='flex justify-end gap-2.5'>
        {getTriggerPrimaryButton(triggerEvent, eventType)}

        <DropdownMenu>
          <DropdownMenu.Trigger>
            <Button variant='outlined' color='default' size='sm' icon={<Icon name='ellipsis' />} onClick={() => Analytics.track('NavBar - Notification - MoreOptions')} />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align='end' portal>
            <DropdownMenu.Item>
              <Link
                to={{
                  pathname: Location.TRIGGERS_EDIT,
                  search: `?triggerId=${trigger.id}`,
                }}
                onClick={() => {
                  Analytics.track('NavBar - Notification - EditRule', {
                    condition: conditionCategory,
                  });
                }}
                className='flex w-full items-center gap-1'
              >
                <DropdownMenu.Icon icon={<Icon name='pencil' />} />
                {t('notifications.editRule')}
              </Link>
            </DropdownMenu.Item>

            <DropdownMenu.Item>
              <Link
                to={path}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() =>
                  Analytics.track('NavBar - Notification - ViewRuleHistory', {
                    condition: conditionCategory,
                  })
                }
                className='flex w-full items-center gap-1'
              >
                <DropdownMenu.Icon icon={<Icon name='list-checks' />} />
                {t('notifications.viewRuleHistory')}
              </Link>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TriggerNotificationItem;
